<template>
  <div class="subscriber-slide">
    <div class="subscriber-slider">
      <div class="slider">
        <div class="slide" :style="slideStyle">
          <DialogSubscriberSlideItem
            :key="index"
            class="item"
            :title="userItems[index].title"
            :description="userItems[index].description"
            :src="imgs[index].src"
            :srcset="imgs[index].srcset"
            :width="imgs[index].width"
            :height="imgs[index].height"
          />
        </div>
        <div ref="dragEl" class="solid" :style="dragStyle"></div>
      </div>
    </div>
    <NavigationDots
      :total="userItems.length"
      :index="index"
      @select="handleSelectFromNav"
    />
  </div>
</template>
<script setup lang="ts">
import { getLevelOfEducationGroup, LevelOfEducationGroup } from '~/lib/level';

const profile = useProfileStore();

const groupId = computed(() => {
  return getLevelOfEducationGroup(profile.levelOfEducationId);
});

const isParent = computed(() => {
  return profile.isParent;
});

const imgs = [
  {
    src: '/images/subscriber/img-1.webp',
    srcset: '/images/subscriber/img-1@2x.webp 2x',
    width: 227,
    height: 179,
  },
  {
    src: '/images/subscriber/img-2.webp',
    srcset: '/images/subscriber/img-2@2x.webp 2x',
    width: 232,
    height: 185,
  },
  {
    src: '/images/subscriber/img-3.webp',
    srcset: '/images/subscriber/img-3@2x.webp 2x',
    width: 272,
    height: 168,
  },
  {
    src: '/images/subscriber/img-4.webp',
    srcset: '/images/subscriber/img-4@2x.webp 2x',
    width: 273,
    height: 181,
  },
  {
    src: '/images/subscriber/img-5.webp',
    srcset: '/images/subscriber/img-5@2x.webp 2x',
    width: 193,
    height: 215,
  },
  {
    src: '/images/subscriber/img-6.webp',
    srcset: '/images/subscriber/img-6@2x.webp 2x',
    width: 109,
    height: 210,
  },
];

const items = {
  parent: [
    {
      title: '💯 Des fiches de révision illustrées',
      description: 'Tout ce qu’il faut retenir dans toutes les matières',
    },
    {
      title: '💯 Des quiz corrigés + exercices interactifs',
      description: 'Pour se tester et réussir sereinement',
    },
    {
      title: '🔥 Des exercices pas à pas',
      description:
        'Fini les "j\'ai eu de la chance", maintenant votre ado sait faire !',
    },
    {
      title: '🚀 Des Annales Brevet corrigées',
      description: 'Tout pour s’entraîner et réussir en confiance',
    },
    {
      title: '',
      description:
        '📗 Des mini-cours <strong>dans toutes les matières</strong>',
    },
    {
      title: '🫶 À partager jusqu’à 5 comptes !',
      description: '',
    },
  ],
  default: [
    {
      title: '💯 Tes fiches de révision illustrées',
      description: '',
    },
    {
      title: ' 🤩 Tes quiz corrigés, flashcards, paires...',
      description: '',
    },
    {
      title: '✍🏻 Tes exos pas à pas corrigés',
      description: '',
    },
    {
      title: '🚀 Tes Annales corrigées',
      description: '',
    },
    {
      title: '📗 Tes mini-cours dans toutes les matières',
      description: '',
    },
  ],
  school_3: [
    {
      title: '💯 Tes fiches de révision illustrées',
      description: '',
    },
    {
      title: ' 🤩 Tes quiz corrigés, flashcards, paires...',
      description: '',
    },
    {
      title: '✍🏻 Tes exercices Brevet corrigés',
      description: '',
    },
    {
      title: '🚀 Tes Annales Brevet corrigées',
      description: '',
    },
    {
      title: '📗 Tes mini-cours dans toutes les matières ',
      description: '',
    },
  ],
  first: [
    {
      title: '💯 Tes fiches de révision illustrées',
      description: '',
    },
    {
      title: '🤩 Tes quiz corrigés, flashcards, paires...',
      description: '',
    },
    {
      title: '✍🏻 Tes exos pas à pas corrigés',
      description: '',
    },
    {
      title: '🚀 Tes Annales Bac de Français corrigées',
      description: '',
    },
    {
      title: '📗 Tes mini-cours dans toutes les matières ',
      description: '',
    },
  ],
  terminal: [
    {
      title: '💯 Tes fiches de révision illustrées',
      description: '',
    },
    {
      title: '🤩 Tes quiz corrigés, flashcards, paires...',
      description: '',
    },
    {
      title: '✍🏻 Tes exos pas à pas corrigés',
      description: '',
    },
    {
      title: '🚀 Tes Annales Bac corrigées',
      description: '',
    },
    {
      title: '📗 Tes mini-cours dans toutes les matières ',
      description: '',
    },
  ],
};

const userItems = computed(() => {
  if (isParent.value) return items.parent;
  switch (groupId.value) {
    case LevelOfEducationGroup.SHOOL_3:
    case LevelOfEducationGroup.First:
    case LevelOfEducationGroup.Terminal:
      return items[groupId.value];
    default:
      return items.default;
  }
});

const { counter, pause: pauseAutomate } = useInterval(3000, { controls: true });

const index = defineModel<number>('index', { default: 0 });

watch(
  () => counter.value,
  (value) => {
    index.value = value % userItems.value.length;
  }
);

const offsetXSpring = ref<number>(0);

const updating = ref<boolean>(false);
const maxDrag = ref<number>(200);
const dragEl = ref<HTMLElement | null>(null);
const config = { capture: true };
const xMove = ref<number>(0);
const xStart = ref<number>(0);
const dragging = ref<boolean>(false);
const ratio = computed(() => {
  const ratio = Math.min(Math.max(xMove.value / maxDrag.value, -1), 1);
  if (Math.abs(ratio) < 0.1) return 0;
  return ratio;
});
const offsetX = computed(() => ratio.value * maxDrag.value);

const completeDrag = () => {
  if (Math.abs(ratio.value) < 0.2) {
    xMove.value = 0;
    return;
  }
  if (ratio.value < 0) {
    next();
  } else if (ratio.value > 0) {
    previous();
  }
  requestAnimationFrame(() => {
    offsetXSpring.value = -offsetXSpring.value * 5;
    xMove.value = 0;
  });
};

const next = () => {
  if (index.value < userItems.value.length - 1) {
    index.value += 1;
  } else {
    index.value = 0;
  }
};
const previous = () => {
  if (index.value > 0) {
    index.value -= 1;
  } else {
    index.value = userItems.value.length - 1;
  }
};

const start = (e) => {
  pauseAutomate();
  dragging.value = true;
  xStart.value = e.touches ? e.touches[0].clientX : e.clientX;
};

const move = (e) => {
  if (!dragging.value) return;
  const x = e.touches ? e.touches[0].clientX : e.clientX;
  xMove.value = x - xStart.value;
  if (ratio.value > 0.2) {
    e.preventDefault();
  }
};

const end = (e) => {
  dragging.value = false;
  completeDrag();
};

useEventListener(dragEl, 'touchstart', start, config);
useEventListener(dragEl, 'touchmove', move, config);
useEventListener(dragEl, 'touchend', end, config);
useEventListener(dragEl, 'mousedown', start, config);
useEventListener(dragEl, 'mousemove', move, config);
useEventListener(dragEl, 'mouseup', end, config);

const dragStyle = computed(() => {
  return {
    transform: `translateX(${xMove.value}px)`,
  };
});
const slideStyle = computed(() => {
  return {
    transform: `translateX(${offsetXSpring.value}px)`,
  };
});

const update = () => {
  updating.value = true;
  offsetXSpring.value += (offsetX.value - offsetXSpring.value) * 0.2;

  if (Math.abs(offsetXSpring.value - offsetX.value) > 1) {
    requestAnimationFrame(() => update());
  } else {
    updating.value = false;
    if (!dragging.value) {
      offsetXSpring.value = 0;
      xMove.value = 0;
    }
  }
};

const handleSelectFromNav = (i: number) => {
  pauseAutomate();
  index.value = i;
};

watch(
  () => offsetX.value,
  (value) => {
    if (!updating.value) {
      update();
    }
  }
);
</script>

<style lang="scss" scoped>
.subscriber-slide {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  gap: var(--gap_n);
  width: 100%;
}
.subscriber-slider {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  width: 100%;
}
.button-desktop {
  display: none;
  @include above('l') {
    display: flex;
  }
}
.solid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  background: red;
  cursor: grab;
}
.slider {
  display: grid;
  position: relative;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  width: 100%;
  height: 26rem;
  margin: 0 auto;
  overflow: hidden;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.fade-enter-active {
  transition: opacity 0.5s ease;
}
.fade-leave-active {
  transition: opacity 0s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
